/**
 * fragrance-card.js
 * Shared add-to-cart handler for the House of Banic Fragrance Wardrobe card.
 * Used by: sections/frount.liquid, snippets/related-products-block.liquid
 *
 * API:
 *   BanicCard.initAddToCart(containerEl)
 *     Wire up all [data-pcs-add] buttons inside containerEl.
 *     Safe to call multiple times — uses data-pcs-wired sentinel.
 *
 *   BanicCard.revealCards(containerEl)
 *     Add .pcs--in to all .pcs__card inside containerEl (makes them visible).
 *     Used by Related Products after AJAX load (frount handles its own reveal via tab JS).
 */
(function () {
  'use strict';

  window.BanicCard = window.BanicCard || {};

  /* ── Add to cart ─────────────────────────────────────────────────────── */
  BanicCard.initAddToCart = function (container) {
    if (!container) return;

    // Find closest toast element for containers that have one (frount has it, RP doesn't)
    var toastEl = container.querySelector('[data-pcs-toast]');
    var toastText = toastEl ? toastEl.querySelector('[data-pcs-toast-text]') : null;

    container.querySelectorAll('[data-pcs-add]:not([data-pcs-wired])').forEach(function (btn) {
      btn.setAttribute('data-pcs-wired', '1');

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        // Prevent duplicate submissions
        if (btn.classList.contains('is-adding') || btn.getAttribute('aria-disabled') === 'true') return;

        var variantId = btn.getAttribute('data-variant-id');
        if (!variantId) return;

        var cartSurface = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        var cartRequestToken = cartSurface && typeof cartSurface.beginAdd === 'function'
          ? cartSurface.beginAdd(btn)
          : null;
        var sections = cartSurface && typeof cartSurface.getSectionsToRender === 'function'
          ? cartSurface.getSectionsToRender().map(function (s) { return s.id; })
          : [];

        // Adding state triggers animation (perfume drop, cart settle)
        var animationStartTime = Date.now();
        btn.classList.add('is-adding');
        btn.setAttribute('aria-busy', 'true');
        btn.setAttribute('aria-disabled', 'true');

        var checkLimitsAndAdd = function() {
          var pendingCartDataPromise = window.CartItems && typeof window.CartItems.fetchCartData === 'function'
            ? window.CartItems.fetchCartData()
            : fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'cart.js').then(function(r) { return r.json(); });

          return pendingCartDataPromise.then(function(cart) {
            if (!cart || !cart.items) return Promise.resolve(true);
            var itemSize = null;
            var tenMlQty = 0;
            var fiftyMlQty = 0;

            for (var i = 0; i < cart.items.length; i++) {
              var checkItem = cart.items[i];
              var checkSize = null;
              if (checkItem.options_with_values) {
                for (var l = 0; l < checkItem.options_with_values.length; l++) {
                  var cVal = (checkItem.options_with_values[l].value || '').replace(/\s/g, '').toLowerCase();
                  if (cVal === '10ml' || cVal === '50ml') { checkSize = cVal; break; }
                }
              }
              if (!checkSize && checkItem.variant_title) {
                var cTitleVal = checkItem.variant_title.replace(/\s/g, '').toLowerCase();
                if (cTitleVal.indexOf('10ml') !== -1) checkSize = '10ml';
                else if (cTitleVal.indexOf('50ml') !== -1) checkSize = '50ml';
              }
              
              if (checkSize === '10ml') tenMlQty += checkItem.quantity;
              if (checkSize === '50ml') fiftyMlQty += checkItem.quantity;
              
              if (checkItem.variant_id == variantId && checkSize) {
                itemSize = checkSize;
              }
            }
            
            if (!itemSize) {
              var card = btn.closest('.card, .product__info-container, .pcs__card');
              if (card) {
                var matchedRadio = card.querySelector('input[data-variant-id="' + variantId + '"]');
                if (matchedRadio) {
                  var sizeVal = (matchedRadio.getAttribute('data-variant-size') || '').replace(/\s/g, '').toLowerCase();
                  if (sizeVal === '10ml' || sizeVal === '50ml') itemSize = sizeVal;
                } else {
                  var cardText = card.textContent.replace(/\s/g, '').toLowerCase();
                  var btnTitle = (btn.getAttribute('data-product-title') || '').replace(/\s/g, '').toLowerCase();
                  var combinedText = cardText + btnTitle;
                  if (combinedText.indexOf('10ml') !== -1) itemSize = '10ml';
                  else if (combinedText.indexOf('50ml') !== -1) itemSize = '50ml';
                }
              }
            }
            
            if ((itemSize === '10ml' || itemSize === '50ml' || tenMlQty > 0 || fiftyMlQty > 0) && (tenMlQty + fiftyMlQty + 1 > 16)) {
              return Promise.reject(new Error("Your fragrance edit can include up to 16 bottles per order."));
            }
            if (itemSize === '10ml' && tenMlQty + 1 > 8) {
              return Promise.reject(new Error("You can include up to 8 × 10 ml fragrances in one order."));
            }
            if (itemSize === '50ml' && fiftyMlQty + 1 > 8) {
              return Promise.reject(new Error("You can include up to 8 × 50 ml fragrances in one order."));
            }
            
            return Promise.resolve(true);
          });
        };

        var addRequest = function () {
          return fetch(
            (window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'cart/add.js',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                items: [{ id: parseInt(variantId, 10), quantity: 1 }],
                sections: sections,
                sections_url: window.location.pathname
              })
            }
          );
        };

        var addResponse = checkLimitsAndAdd().then(function() {
          return cartSurface && typeof cartSurface.queueCartRequest === 'function'
            ? cartSurface.queueCartRequest(addRequest)
            : addRequest();
        });

        addResponse
          .then(function (res) {
            return res.json().then(function (data) {
              if (!res.ok) return Promise.reject(data);
              return data;
            });
          })
          .then(function (data) {
            // Wait for drop animation to finish (at least 440ms) before showing success check
            var elapsed = Date.now() - animationStartTime;
            var delay = Math.max(0, 440 - elapsed);

            setTimeout(function () {
              btn.classList.remove('is-adding');
              btn.classList.add('is-success');

              if (cartSurface && typeof cartSurface.renderContents === 'function') {
                if (!cartRequestToken) cartSurface.setActiveElement(btn);
                cartSurface.renderContents(data, cartRequestToken);
              } else if (toastEl) {
                if (toastText) toastText.textContent = 'Added to your selection';
                toastEl.classList.add('is-visible');
                setTimeout(function () { toastEl.classList.remove('is-visible'); }, 2200);
              }

              // Keep success check visible for ~800ms
              setTimeout(function () { btn.classList.remove('is-success'); }, 800);
              document.dispatchEvent(new CustomEvent('cart:refresh'));
            }, delay);
          })
          .catch(function (error) {
            // Revert adding state immediately on error
            btn.classList.remove('is-adding');
            var rawMsg = error.description || error.message || '';
            var message = "We couldn't update your selection. Please try again.";
            var descriptionLower = rawMsg.toLowerCase();
            
            if (descriptionLower.indexOf('sold out') !== -1 || descriptionLower.indexOf('all available') !== -1 || descriptionLower.indexOf('not enough') !== -1) {
              message = 'This fragrance is currently unavailable in the selected size.';
            } else if (descriptionLower.indexOf("can't add more") !== -1 || descriptionLower.indexOf('limit') !== -1 || descriptionLower.indexOf('not currently available') !== -1 || descriptionLower.indexOf('inventory') !== -1 || descriptionLower.indexOf('exceeds') !== -1) {
              message = 'The requested quantity is not currently available.';
            }
            
            if (rawMsg.indexOf('fragrance edit can include') !== -1 || rawMsg.indexOf('fragrances in one order') !== -1) {
              message = rawMsg;
            }
            if (cartSurface && typeof cartSurface.showError === 'function') {
              cartSurface.showError(message, cartRequestToken);
            } else if (toastEl) {
              if (toastText) toastText.textContent = message;
              toastEl.classList.add('is-visible');
              setTimeout(function () {
                toastEl.classList.remove('is-visible');
                if (toastText) toastText.textContent = 'Added to your selection';
              }, 2600);
            }
          })
          .finally(function () {
            if (cartSurface && typeof cartSurface.finishAdd === 'function') {
              cartSurface.finishAdd(cartRequestToken);
            }
            btn.removeAttribute('aria-busy');
            btn.removeAttribute('aria-disabled');
          });
      });
    });
  };

  /* ── Reveal cards (for contexts without JS-driven reveal) ─────────────── */
  BanicCard.revealCards = function (container) {
    if (!container) return;
    container.querySelectorAll('.pcs__card:not(.pcs--in)').forEach(function (card) {
      card.classList.add('pcs--in');
    });
  };

  /* ── Cursor-reactive glow + subtle tilt (card media) ─────────────────── */
  BanicCard.initTilt = function (container) {
    if (!container) return;
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || !window.matchMedia('(hover: hover)').matches) return;

    container.querySelectorAll('[data-pcs-tilt]').forEach(function (media) {
      var glow = media.querySelector('[data-pcs-glow]');
      media.addEventListener('mousemove', function (e) {
        var rect = media.getBoundingClientRect();
        var px = (e.clientX - rect.left) / rect.width;
        var py = (e.clientY - rect.top) / rect.height;
        if (glow) {
          glow.style.setProperty('--gx', (px * 100) + '%');
          glow.style.setProperty('--gy', (py * 100) + '%');
        }
        media.style.transform = 'rotateX(' + ((0.5 - py) * 10) + 'deg) rotateY(' + ((px - 0.5) * 10) + 'deg)';
      });
      media.addEventListener('mouseleave', function () {
        media.style.transform = 'rotateX(0deg) rotateY(0deg)';
      });
    });
  };

  /* ── Auto-init for product-recommendations AJAX ───────────────────────── */
  function onRecommendationsLoaded(el) {
    BanicCard.initAddToCart(el);
    BanicCard.initTilt(el);
  }

  function observeRecommendations() {
    document.querySelectorAll('product-recommendations').forEach(function (el) {
      // If already loaded (e.g. Shopify cached the response), init immediately
      if (el.querySelector('.pcs__card')) {
        onRecommendationsLoaded(el);
        return;
      }
      // Otherwise watch for the AJAX innerHTML replacement
      var observer = new MutationObserver(function (mutations, obs) {
        if (el.querySelector('.pcs__card')) {
          obs.disconnect();
          onRecommendationsLoaded(el);
        }
      });
      observer.observe(el, { childList: true, subtree: true });
    });
  }

  function initAllBanicCards() {
    BanicCard.initAddToCart(document);
    BanicCard.initTilt(document);
    observeRecommendations();
    
    // Watch for dynamically added product cards (e.g., from collection pagination/filters)
    var globalObserver = new MutationObserver(function(mutations) {
      var shouldInit = false;
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes.length > 0) {
          shouldInit = true;
          break;
        }
      }
      if (shouldInit) {
        BanicCard.initAddToCart(document);
      }
    });
    globalObserver.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllBanicCards);
  } else {
    initAllBanicCards();
  }

})();
